import { Api } from '$api';
import { all, takeLatest, retry, put, call, select } from 'redux-saga/effects';
import { adsActions, adsSelector } from './index';
import { batchActions } from '$store';
import { toastActions } from '$store/toast';
import { CreatePromoPostAction, CreatePromoProfileAction, CreateAdAction } from './index';
import { showModal } from '$navigation/helper';
import { AD_TARGETING } from '$shared/constants/modals';
import { PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '$store/rootReducer';
import { createPostFormSelector, postsActions } from '$store/posts';
import { attachActions, attachItemsSelector } from '$store/attach';
import { t } from '$translation';
import { AdsUsersLoadAction } from '$store/ads/interface';
import { mainActions } from '$store/main';
import { followersActions } from '$store/followers';

function* loadAdsWorker() {
  try {
    const { ads, balance, placeBalance } = yield call(Api.get, '/ads');
    yield put(
      batchActions(
        adsActions.setAds({ ads, balance, placeBalance }),
        mainActions.setPlaceBalance(placeBalance ?? 0),
      ),
    );
  } catch (e) {
    console.error(e);
    yield put(adsActions.setAdsFailed());
  }
}

function* createPromoProfileWorker(action: CreatePromoProfileAction) {
  try {
    const { cover, avatar } = action.payload;
    yield put(toastActions.setToastLoading());
    const { promoProfile } = yield call(Api.post, '/ads/promo/profile/create', {
      cover,
      avatar,
    });
    yield put(
      batchActions(
        adsActions.setAdType('profile'),
        adsActions.setObjectId(promoProfile.id),
        toastActions.hideToast(),
      ),
    );
    yield call(showModal, AD_TARGETING);
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* createPromoPostWorker() {
  try {
    const form = yield select((state: RootState) =>
      createPostFormSelector(state, 'post_ad_new'),
    );
    const attachments = yield select((state: RootState) =>
      attachItemsSelector(state, 'post_ad_new'),
    );

    if (attachments === 'photo_uploading') {
      return yield put(toastActions.setToastFail(t('photo_uploading_err')));
    }

    if (form.text.length === 0 && !attachments.length) {
      return yield put(toastActions.setToastFail(t('im_empty_error')));
    }

    yield put(toastActions.setToastLoading());
    const { promoPost } = yield call(Api.post, '/ads/promo/post/create', {
      text: form.text.trim(),
      attachments,
    });

    yield put(
      postsActions.updateFormText({
        formId: 'post_ad_new',
        text: '',
      }),
    );
    yield put(attachActions.clear('post_ad_new'));

    yield put(adsActions.setAdType('promo_post'));
    yield put(adsActions.setObjectId(promoPost.id));
    yield put(toastActions.hideToast());
    yield call(showModal, AD_TARGETING);
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* createAdWorker(action: CreateAdAction) {
  try {
    const { price, spendLimit, targetSubscribed, targetLang, name, adId, currency } =
      action.payload;
    const { adType, objectId } = yield select(adsSelector);

    yield put(toastActions.setToastLoading());
    const { ads, ad } = yield call(
      Api.post,
      adId > 0 ? `/ads/${adId}/save_ad` : '/ads/create',
      {
        adType,
        objectId,
        price,
        spendLimit,
        targetSubscribed,
        targetLang,
        name,
        currency,
      },
    );

    const info = adId > 0 ? ad : ads;

    if (adId > 0) {
      yield put(adsActions.setAd(info));
    } else {
      yield put(adsActions.addAd(info));
    }
    yield put(toastActions.hideToast());
    yield call(showModal, `ad${info.id}`, true);
  } catch (e) {
    return yield put(toastActions.setToastFail(e.message));
  }
}

function* sendViewWorker(action: PayloadAction<number>) {
  try {
    yield call(Api.post, `/ads/${action.payload}/track`);
  } catch (e) {}
}

function* loadUsersWorker(action: AdsUsersLoadAction) {
  try {
    const data = yield call(Api.get, `/ads/${action.payload}/users`);
    yield put(
      batchActions(
        adsActions.loadUsersLoaded(data.userIds),
        mainActions.setUsers(data.users),
        followersActions.setFollowing(data.followed),
      ),
    );
  } catch (e) {
    yield put(
      batchActions(adsActions.loadUsersFailed(), toastActions.setToastFail(e.message)),
    );
  }
}

export function* adsSaga() {
  yield all([
    takeLatest(adsActions.loadAds, loadAdsWorker),
    takeLatest(adsActions.createPromoProfile, createPromoProfileWorker),
    takeLatest(adsActions.createPromoPost, createPromoPostWorker),
    takeLatest(adsActions.createAd, createAdWorker),
    takeLatest(adsActions.sendView, sendViewWorker),
    takeLatest(adsActions.loadUsers, loadUsersWorker),
  ]);
}
